import type {
  FactoryOptionDto,
  ItemPriceOnlyDto,
  MachineAttachmentItemPriceOnlyDto,
  MachineItemPriceOnlyDto
} from '@api/machines'
import {
  calculateSectionTotal,
  displayOldPrice,
  matchOldPriceItem
} from '@common/utils'
import { MolsonBox, MolsonTypography } from '@components/reusable'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { BreakdownItem } from '../breakdown-item/BreakdownItem'
import { BreakdownSection } from '../breakdown-section/BreakdownSection'

type SpecBreakdownBaseUnitSectionProps = {
  baseUnit: FactoryOptionDto[]
  LCCpriceValue?: string | number | null
  needsRevising?: boolean
  pdi: number | undefined
  quotationItemOldPrices: MachineAttachmentItemPriceOnlyDto &
    MachineItemPriceOnlyDto
  translationKeyPrefix: string
}

export const SpecBreakdownBaseUnitSection = ({
  baseUnit,
  LCCpriceValue,
  needsRevising = false,
  pdi,
  quotationItemOldPrices,
  translationKeyPrefix
}: SpecBreakdownBaseUnitSectionProps) => {
  const theme = molsonUseTheme()
  const { t } = useTranslation('', {
    keyPrefix: translationKeyPrefix
  })

  const baseUnitItems = baseUnit.filter(
    item => !(item.removed || item.isArchived)
  )
  const baseUnitItemsIds = baseUnitItems.map(baseUnit => baseUnit.id)
  const baseUnitItemOldPrices = quotationItemOldPrices.baseUnit?.filter(
    baseUnit => baseUnitItemsIds.includes(baseUnit.id)
  )

  const hasPdi = pdi !== undefined
  const sectionLength = baseUnitItems.length + (hasPdi ? 1 : 0)

  const sectionTotalCurrentPrice = () => {
    if (LCCpriceValue) {
      return Number(LCCpriceValue)
    }

    return calculateSectionTotal(baseUnitItems) + (pdi ?? 0)
  }

  const sectionTotalOldPrice =
    calculateSectionTotal(baseUnitItemOldPrices ?? []) +
    (quotationItemOldPrices.pdi ?? 0)

  const calculateOldPrice = () => {
    return displayOldPrice(sectionTotalCurrentPrice(), sectionTotalOldPrice)
  }

  const individualItemPrice = (
    itemPrice?: number,
    oldItemPrice?: number,
    lcCprice?: number | string | null
  ) => {
    if (lcCprice) {
      return undefined
    }

    if (itemPrice) {
      return oldItemPrice || itemPrice !== 0 ? itemPrice : undefined
    }
  }

  const baseUnitItemsChange = (
    newList: FactoryOptionDto[],
    oldList: ItemPriceOnlyDto[]
  ) => {
    const oldMap = new Map(oldList.map(item => [item.id, item]))

    const hasPriceChange = newList.some(newItem => {
      const oldItem = oldMap.get(newItem.id)
      return oldItem && newItem.dnp !== oldItem.dnp && !newItem.isNewItem
    })

    const hasNewItems = newList.some(newItem => newItem.isNewItem)

    return {
      priceChange: hasPriceChange || (hasPriceChange && hasNewItems),
      itemsUpdate: hasNewItems
    }
  }

  const renderBaseUnitChangeNotification = () => {
    const { priceChange, itemsUpdate } = baseUnitItemsChange(
      baseUnitItems,
      baseUnitItemOldPrices ?? []
    )

    if (priceChange || itemsUpdate) {
      return (
        <MolsonBox>
          <MolsonTypography color={theme.palette.primary.error}>
            {priceChange ? t('baseUnitPriceChanged') : t('baseUnitListUpdated')}
          </MolsonTypography>
        </MolsonBox>
      )
    }
    return null
  }

  return sectionLength > 0 ? (
    <BreakdownSection
      firstInList={!needsRevising}
      highlightedSection={true}
      oldPrice={LCCpriceValue ? undefined : calculateOldPrice()}
      price={sectionTotalCurrentPrice()}
      title={`${t('baseUnit')} (${sectionLength})`}
    >
      {renderBaseUnitChangeNotification()}

      {baseUnitItems.map(item => {
        const oldPrice = matchOldPriceItem(quotationItemOldPrices, {
          id: item.id ?? 0,
          price: item.dnp ?? 0,
          type: 'baseUnit'
        })

        const itemPrice = item.dnp ?? undefined

        return (
          <BreakdownItem
            key={`spec_breakdown_base_unit_${item.id}`}
            name={item.name ?? ''}
            oldPrice={oldPrice}
            price={individualItemPrice(itemPrice, oldPrice, LCCpriceValue)}
          />
        )
      })}

      {hasPdi && (
        <BreakdownItem
          name={t('pdi')}
          oldPrice={displayOldPrice(pdi, quotationItemOldPrices.pdi ?? 0)}
          price={pdi}
        />
      )}
    </BreakdownSection>
  ) : null
}
